"use client";

import { ComposerPrimitive, type QueueItemState, useAui, useAuiState } from "@assistant-ui/react";
import { CornerDownLeftIcon, PaperclipIcon, PencilIcon, XIcon } from "lucide-react";
import type { FC } from "react";
import { mergeIntoDraft, queueItemFiles, queueItemText } from "@/lib/composer-queue";
import { stopAnswer } from "@/lib/run-control";
import { threadIdFor } from "@/lib/thread-id";
import { TooltipIconButton } from "./tooltip-icon-button";

/**
 * Messages sent while the model answers wait here, above the composer, and go out in order once
 * it's done (ADMIN-212, assistant-ui's message queue). Each can be sent now (it stops the answer
 * and goes next), taken back into the composer to edit, or removed.
 */
export const ComposerQueue: FC = () => {
  return (
    <ComposerPrimitive.Queue>
      {({ queueItem }) => <QueuedMessage queueItem={queueItem} />}
    </ComposerPrimitive.Queue>
  );
};

const QueuedMessage: FC<{ queueItem: QueueItemState }> = ({ queueItem }) => {
  const aui = useAui();
  const text = queueItemText(queueItem.parts);
  const files = queueItem.parts.filter((p) => p.type === "file").length;
  const item = () => aui.composer().queueItem({ id: queueItem.id });
  // The id the thread's runtime runs under (Layout.tsx → useAgentThread).
  const threadId = useAuiState(
    (s: { threadListItem: { id: string; remoteId: string | undefined } }) =>
      s.threadListItem.remoteId ?? threadIdFor(s.threadListItem.id),
  );

  // To the front of the queue, then stop the answer: the queue sends it next.
  const sendNow = () => {
    item().move({ lane: "steer", insertAfter: null });
    if (aui.thread().getState().isRunning) stopAnswer(threadId);
  };

  const edit = () => {
    const composer = aui.composer();
    const restored = queueItemFiles(queueItem.parts);
    item().remove();
    composer.setText(mergeIntoDraft(text, composer.getState().text));
    for (const file of restored) void composer.addAttachment(file);
  };

  return (
    <div
      data-slot="composer-queue-item"
      className="border-border bg-muted/40 text-foreground flex items-center gap-2 border border-b-0 px-3 py-1.5 text-sm first:mt-0"
    >
      <span className="text-muted-foreground shrink-0 text-xs">Queued</span>
      <span className="min-w-0 flex-1 truncate" title={text}>
        {text || "(attachment)"}
      </span>
      {files > 0 && (
        <span className="text-muted-foreground flex shrink-0 items-center gap-0.5 text-xs">
          <PaperclipIcon className="size-3" />
          {files}
        </span>
      )}
      <div className="flex shrink-0 items-center gap-0.5">
        <TooltipIconButton
          tooltip="Send now"
          side="top"
          className="size-6"
          aria-label="Send now"
          onClick={sendNow}
        >
          <CornerDownLeftIcon className="size-3.5" />
        </TooltipIconButton>
        <TooltipIconButton
          tooltip="Edit"
          side="top"
          className="size-6"
          aria-label="Edit queued message"
          onClick={edit}
        >
          <PencilIcon className="size-3.5" />
        </TooltipIconButton>
        <TooltipIconButton
          tooltip="Remove"
          side="top"
          className="size-6"
          aria-label="Remove queued message"
          onClick={() => item().remove()}
        >
          <XIcon className="size-3.5" />
        </TooltipIconButton>
      </div>
    </div>
  );
};
